// Eskom PARCC — AI review on the Compliance Evaluation Sheet (Surface A).
//
// The engine reads the transaction's staged evidence and PROPOSES a verdict per
// criterion. Nothing is recorded: proposals pre-fill the rows the reviewer is
// already editing, and the existing Save stays the only writer. Confirming a
// proposal simply sets the status field the reviewer would have set by hand.
//
// GATING. Renders nothing at all unless the person is both granted
// (AccessGrant.aiEnabled) and opted in (Settings → AI Mode). With AI off the CES
// screen is byte-for-byte what it was before the engine existed — asserted in
// test/features/ces/CesAiPanel.test.tsx, because "additive" is a claim that
// decays silently.
//
// STYLING. `.ai-*` rules live UNPREFIXED in components.css: CesPage is shared by
// Legacy and Lumen, so a `[data-ui='lumen']` prefix would leave Legacy unstyled
// and no test would catch it (theme parity covers the shell and the shared
// primitives, not feature pages).
import { useEffect, useState } from 'react';
import {
  Sparkles,
  AlertTriangle,
  Check,
  RefreshCw,
  Lock,
  Search,
  Ban,
  Clock,
  Maximize2,
} from 'lucide-react';
import { Button, Flyout } from '../../components/ui';
import { Badge, complianceClass } from '../../components/ui/Badge';
import { cesMapStatusToRegister } from '../../domain/ces';
import { type AiProposal } from '../../lib/adminApi';
import { useAiAvailability } from '../../lib/aiMode';
import { useAiReviewRun } from './useAiReviewRun';

export type ProposalMap = Map<number, AiProposal>;

/** Confidence banding. Kept as one function so the bar, the label and the
 *  "needs attention" count can never disagree with each other. */
export function confidenceBand(c: number): 'High' | 'Medium' | 'Low' {
  if (c >= 0.8) return 'High';
  if (c >= 0.5) return 'Medium';
  return 'Low';
}

/** The plain-English line shown while a run is in flight — the step actually in
 *  progress, or the last one that completed. The mono call string is never shown;
 *  `detail` is metadata-only by contract (see AiRunStep). */
function currentActivity(run: import('../../lib/adminApi').AiRun): string | null {
  const running = run.steps.find((s) => s.status === 'running');
  if (running) return running.detail;
  const last = [...run.steps].reverse().find((s) => s.status === 'ok' || s.status === 'warn');
  return last?.detail ?? null;
}

/** How a reviewer must ACT on a proposal — the axis the summary groups on.
 *  Deliberately not the verdict itself: "flagged, and evidenced" and "flagged
 *  only because the document was missing" demand different work, and the reviewer
 *  needs to see that split before they see anything else. */
type ActionGroup = 'attention' | 'missing' | 'compliant' | 'na';

function classifyProposal(p: AiProposal): ActionGroup {
  // A missing-evidence default is its own category regardless of the verdict it
  // produced: the action is to locate the document, not to contest a finding.
  if (p.missingEvidence) return 'missing';
  const register = cesMapStatusToRegister(p.verdict).toLowerCase();
  if (register.includes('not applicable')) return 'na';
  if (register.includes('compliant') && !register.includes('non-compli')) return 'compliant';
  // Every evidenced non-compliance level, plus anything the mapping could not
  // place ("Pending Review"), routes to attention — the safe default is to make
  // a human look.
  return 'attention';
}

/** Group presentation, in the order the reviewer should work them. */
const GROUP_META: Record<
  ActionGroup,
  { title: string; blurb: string; tone: string; openByDefault: boolean }
> = {
  attention: {
    title: 'Needs your attention',
    blurb: 'Evidenced non-compliance the engine flagged — read the citation, then confirm or override.',
    tone: 'attention',
    openByDefault: true,
  },
  missing: {
    title: 'Evidence not found',
    blurb:
      'Required documents were not in the pack, so the verdict defaulted to possible irregular expenditure. Locate the evidence or confirm the gap.',
    tone: 'missing',
    openByDefault: true,
  },
  compliant: {
    title: 'Looks compliant',
    blurb: 'Backed by cited evidence. Skim the citations and accept in one click.',
    tone: 'compliant',
    openByDefault: false,
  },
  na: {
    title: 'Not applicable',
    blurb: 'The engine judged these out of scope for this procurement route.',
    tone: 'na',
    openByDefault: false,
  },
};

const GROUP_ORDER: ActionGroup[] = ['attention', 'missing', 'compliant', 'na'];

export function AiReviewPanel({
  referenceNo,
  criteria,
  disabled,
  onProposals,
  onHolistic,
  onRunMeta,
  decidedCount = 0,
  decidedSeqs,
  onConfirm,
  onOpenCitation,
  sortByConfidence = false,
  onToggleSort,
}: {
  referenceNo: string;
  /** The rubric rows to assess, already resolved by the page. */
  criteria: { seq: number; label: string; anchors?: string[] }[];
  disabled: boolean;
  onProposals: (map: ProposalMap) => void;
  /** Findings raised outside the checklist, rendered after the criteria table. */
  onHolistic?: (findings: AiProposal[]) => void;
  /** The run's id + answering model, for provenance on a confirmed proposal. */
  onRunMeta?: (meta: { runId: string; model: string | null } | null) => void;
  /** How many proposed criteria the reviewer has already decided — owned by the
   *  page, which holds the row state. */
  decidedCount?: number;
  /** Which proposed criteria are already actioned, so the summary greys them. */
  decidedSeqs?: ReadonlySet<number>;
  /** Confirm proposals — all undecided, or a specific set of seqs. */
  onConfirm?: (seqs?: number[]) => void;
  /** Open the evidence viewer at a citation. */
  onOpenCitation?: (citation: AiProposal['citations'][number]) => void;
  sortByConfidence?: boolean;
  onToggleSort?: () => void;
}): React.JSX.Element | null {
  const ai = useAiAvailability();
  // `enabled` gates the hook's on-mount reads (rehydrating the last review) to
  // people who actually have the engine — otherwise every CES mount would fire a
  // 403 for a non-AI user.
  const { state, start, cancel } = useAiReviewRun(referenceNo, ai.available);

  // Proposals arrive incrementally, one batch at a time, so they are published
  // to the page as they land rather than only at the end — a reviewer can start
  // confirming batch one while batch four is still running.
  const run = 'run' in state ? state.run : null;
  const proposalCount = run?.proposals.length ?? 0;
  useEffect(() => {
    if (!run) return;
    const map: ProposalMap = new Map();
    // Holistic findings attach to no criterion, so they cannot be keyed by seq —
    // they are published separately and rendered after the criteria table.
    for (const p of run.proposals) {
      if (p.seq !== null && !p.holistic) map.set(p.seq, p);
    }
    onProposals(map);
    onHolistic?.(run.proposals.filter((p) => p.holistic === true));
    // The answering model for provenance — the most recent batch's, since a
    // quota refusal can swap Claude for Gemini mid-run.
    const model = [...(run.batchesRun ?? [])].reverse().find((b) => b.model)?.model ?? null;
    onRunMeta?.({ runId: run.runId, model });
    // Keyed on the COUNT, not the array: the run object is replaced on every
    // step, and depending on it would republish an identical map each tick.
  }, [proposalCount, run, onProposals, onHolistic, onRunMeta]);

  // Expand lifts the finished review into a full-height Flyout. Reset it whenever
  // the panel leaves its done state (a re-run, an error) so a stale overlay can
  // never linger over a review that is no longer on screen.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (state.kind !== 'done') setExpanded(false);
  }, [state.kind]);

  // Absent, not disabled — the whole point of the per-person rollout.
  if (!ai.available) return null;

  const busy = state.kind === 'running' || state.kind === 'starting' || state.kind === 'stalled';
  // A background run advances server-side; the client only polls it. Distinct
  // from `busy` so the copy can say "you can close this tab" and the client is
  // clearly not the one spending.
  const backgrounded = state.kind === 'background';
  const active = busy || backgrounded;
  const done = state.kind === 'done';
  // How this finished view came to be: a live run just now, a reused unchanged
  // pack, or an earlier review rehydrated on mount. Drives the banner + whether a
  // re-run should force past reuse.
  const source = state.kind === 'done' ? (state.source ?? 'live') : undefined;
  const reused = source === 'reused' || source === 'rehydrated';
  const resultAt = done && run?.finishedAt ? new Date(run.finishedAt) : null;

  // Criterion proposals only: the tiles report progress through the CHECKLIST,
  // and a holistic finding has no criterion row to be confirmed against.
  const list = (run?.proposals ?? []).filter((p) => !p.holistic);
  const meanPct =
    list.length === 0
      ? 0
      : Math.round((list.reduce((s, p) => s + p.confidence, 0) / list.length) * 100);
  // "Below 80%" expressed through confidenceBand rather than re-stating the
  // threshold — the band function exists precisely so the bar, the label and
  // this count cannot drift apart.
  const needAttention = list.filter((p) => confidenceBand(p.confidence) !== 'High').length;
  const modelsUsed = [...new Set((run?.batchesRun ?? []).map((b) => b.model).filter(Boolean))].join(
    ' + ',
  );

  // COVERAGE — the transparency the 13 Aug run lacked: how much of the checklist
  // the engine actually assessed, and what it could not. `criteria` is the full
  // planned checklist; a criterion is either assessed (a proposal), reserved for
  // the reviewer, discarded (attempted but the guard could not ground it), or not
  // assessed at all. The four sum to the checklist, so a blank row is never again
  // indistinguishable from a failure.
  const totalCriteria = criteria.length;
  const proposedSeqs = new Set(list.map((p) => p.seq).filter((s): s is number => s != null));
  const rejectedSeqs = new Set(
    (run?.rejected ?? []).map((r) => r.seq).filter((s): s is number => s != null),
  );
  const reviewerOnlyCount = run?.reviewerOnly?.length ?? 0;
  const discardedCount = [...rejectedSeqs].filter((s) => !proposedSeqs.has(s)).length;
  const notAssessedCount = Math.max(
    0,
    totalCriteria - proposedSeqs.size - reviewerOnlyCount - discardedCount,
  );

  // The finished-review body — reuse note, stat tiles, the grouped proposals and
  // the run actions. Rendered inline by default, or lifted into the Expand Flyout;
  // defined once so the inline surface and the full view are byte-for-byte the same.
  const renderResult = (): React.JSX.Element | null =>
    done && run ? (
      <>
        {reused && (
          // This finished view was NOT paid for now: either an unchanged pack
          // was reused, or an earlier review was rehydrated on mount. Say so,
          // so "Confirmed 0/33" doesn't read as a review that just ran.
          <div className="ais-reuse-note">
            <Clock size={12} aria-hidden />
            {source === 'reused'
              ? 'Reusing your earlier review — the evidence pack is unchanged.'
              : 'Showing your last review.'}
            {resultAt ? ` From ${resultAt.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}.` : ''}
            {' '}Re-run above for a fresh pass.
          </div>
        )}
        <div className="ai-stats">
          <div className="ai-stat">
            <div className="ai-stat-k">Assessed</div>
            <div className="ai-stat-v">
              {proposedSeqs.size}/{totalCriteria}
            </div>
            {/* The rest of the checklist, accounted for — never a silent blank. */}
            {(notAssessedCount > 0 || reviewerOnlyCount > 0 || discardedCount > 0) && (
              <div className="ai-stat-sub">
                {[
                  notAssessedCount > 0 ? `${notAssessedCount} not assessed` : null,
                  discardedCount > 0 ? `${discardedCount} discarded` : null,
                  reviewerOnlyCount > 0 ? `${reviewerOnlyCount} reviewer` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
          <div className="ai-stat">
            <div className="ai-stat-k">Confirmed</div>
            {/* Denominator is the criterion proposals, NOT run.proposals — the
                latter includes holistic findings, so it could never reach 100%. */}
            <div className="ai-stat-v">
              {decidedCount}/{list.length}
            </div>
          </div>
          <div className="ai-stat">
            <div className="ai-stat-k">Mean confidence</div>
            <div className="ai-stat-v">{meanPct}%</div>
          </div>
          <div className="ai-stat">
            <div className="ai-stat-k">Need attention</div>
            {/* Derived through confidenceBand, never a second `< 0.8`
                literal — the bar, the label and this count must not be able
                to disagree. */}
            <div className={`ai-stat-v${needAttention > 0 ? ' warn' : ''}`}>{needAttention}</div>
          </div>
          <div className="ai-stat">
            <div className="ai-stat-k">Model</div>
            {/* PER BATCH. A quota refusal parks Claude and swaps to Gemini
                mid-run, so naming one model would misattribute the verdicts
                the other produced. */}
            <div className="ai-stat-v model">{modelsUsed || '—'}</div>
          </div>
        </div>
        <span>
          <b>{run.proposals.length}</b> proposal{run.proposals.length === 1 ? '' : 's'}
          {reused ? '' : ` in ${Math.round(run.elapsedMs / 1000)}s`}
          {run.status === 'cancelled' ? ' (stopped early)' : ''}
        </span>
        {run.rejected.length > 0 && (
          // Surfaced, never silent: a dropped proposal means the engine could
          // not evidence its own claim, which the reviewer needs to know.
          <span className="ai-warn">
            <AlertTriangle size={12} aria-hidden /> {run.rejected.length} could not be evidenced
            and were discarded
          </span>
        )}
        {run.flaggedDocuments.length > 0 && (
          <span className="ai-bad">
            <AlertTriangle size={12} aria-hidden /> {run.flaggedDocuments.length} document
            {run.flaggedDocuments.length === 1 ? '' : 's'} flagged as suspicious
          </span>
        )}
        {/* The intermediate view. The 33 proposals are grouped by what the
            reviewer must DO with them — evidenced non-compliance first,
            missing-evidence defaults next, then the reassuring groups folded
            away — so the analysis is legible without scrolling the working
            paper row by row. Every item still confirms into the same rows. */}
        {list.length > 0 && (
          <AiProposalSummary
            proposals={list}
            criteria={criteria}
            decidedSeqs={decidedSeqs}
            disabled={disabled}
            onConfirm={onConfirm}
            onOpenCitation={onOpenCitation}
          />
        )}

        {list.length > 0 && (
          <div className="ai-run-actions">
            {onConfirm && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onConfirm()}
                disabled={disabled}
              >
                Confirm all proposals
              </Button>
            )}
            {onToggleSort && (
              <Button size="sm" aria-pressed={sortByConfidence} onClick={onToggleSort}>
                {sortByConfidence
                  ? 'Sorted: lowest confidence first'
                  : 'Sort by confidence'}
              </Button>
            )}
          </div>
        )}
      </>
    ) : null;

  return (
    <div className="ai-panel">
      <div className="ai-panel-head">
        <span className="ai-panel-icon" aria-hidden>
          <Sparkles size={15} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ai-panel-title">AI review</div>
          <div className="small">
            {backgrounded
              ? 'Running in the background — you can close this tab. We’ll email you when it’s done.'
              : busy
                ? 'Reading the staged evidence and weighing each criterion — this takes a minute or two.'
                : 'Proposes a verdict per criterion from the staged evidence. You confirm or override every one — nothing is recorded until you save.'}
          </div>
        </div>
        <div className="ai-panel-actions">
          {done && (
            // Lift the finished review into a full-height view — the analysis
            // gets room to breathe without the reviewer scrolling the working
            // paper. It confirms into the same rows; nothing new is recorded.
            <Button variant="default" onClick={() => setExpanded(true)}>
              <Maximize2 size={14} aria-hidden /> Expand
            </Button>
          )}
          {active && (
            // "Stop after this step", not "Stop": the batch in flight is already
            // running and already billed, and a control that implied otherwise
            // would be contradicted by the invoice. Works for a background run too
            // — the server flag stops the sweep at the next boundary.
            <Button variant="default" onClick={() => void cancel()} disabled={run?.cancelRequested}>
              {run?.cancelRequested ? 'Stopping…' : 'Stop after this step'}
            </Button>
          )}
          {/* Run without watching. `force: done` so a re-run from a shown result
              actually re-runs rather than silently reusing it again. */}
          {!active && (
            <Button
              variant="default"
              onClick={() => void start(criteria, { background: true, force: done })}
              disabled={disabled}
            >
              <Clock size={14} aria-hidden /> Run in the background
            </Button>
          )}
          <Button
            variant={done ? 'default' : 'primary'}
            onClick={() => void start(criteria, { force: done })}
            disabled={disabled || active}
          >
            {active ? (
              <>
                <RefreshCw size={14} aria-hidden className="ai-spin" /> Reviewing…
              </>
            ) : done ? (
              <>
                <RefreshCw size={14} aria-hidden /> Re-run
              </>
            ) : (
              <>
                <Sparkles size={14} aria-hidden /> Run AI review
              </>
            )}
          </Button>
        </div>
      </div>

      {/* WHILE RUNNING: a calm progress line in plain English, not the raw
          tool-call firehose. The reviewer sees WHAT is happening and how far
          along, not `read_ocr_cache(...)`. The full step list is developer
          detail — it moves into a collapsed disclosure once the run is done. */}
      {active && run && (
        <div className="ai-progress" aria-live="polite">
          <div className="ai-progress-bar" aria-hidden>
            <i style={{ width: `${Math.round((run.cursor / Math.max(1, run.totalBatches)) * 100)}%` }} />
          </div>
          <div className="ai-progress-line">
            {backgrounded ? <Clock size={13} aria-hidden /> : <RefreshCw size={13} aria-hidden className="ai-spin" />}
            <span>
              {backgrounded
                ? 'Running in the background…'
                : (currentActivity(run) ?? 'Preparing the review…')}
            </span>
            <span className="ai-progress-count">
              step {Math.min(run.cursor + 1, run.totalBatches)} of {run.totalBatches}
            </span>
          </div>
        </div>
      )}

      {/* DONE: the step-by-step trace is the audit record, kept but folded away.
          Plain-English lines only — the mono call strings are dropped, since a
          reviewer never needs to read `select_evidence(seq=…)`. */}
      {done && run && run.steps.length > 0 && (
        <details className="ai-trace-details">
          <summary>How the engine worked — {run.steps.length} steps</summary>
          <ol className="ai-trace">
            {run.steps.map((s) => (
              <li key={s.id} className={`ai-trace-row is-${s.status}`}>
                <span className="ai-trace-mark" aria-hidden>
                  {s.status === 'ok' ? (
                    <Check size={11} />
                  ) : s.status === 'warn' || s.status === 'error' ? (
                    <AlertTriangle size={11} />
                  ) : (
                    <span className="ai-trace-dot" />
                  )}
                </span>
                <span className="ai-trace-detail">{s.detail}</span>
              </li>
            ))}
          </ol>
        </details>
      )}

      {/* aria-live so a screen-reader user is told the run finished — the visual
          state change is otherwise silent, and the run takes a minute or more. */}
      {/* Idle: the standing compliance note, so the reviewer knows where the
          evidence lives and what the engine is allowed to do, before they spend
          anything. */}
      {state.kind === 'idle' && (
        <div className="ai-compliance-note">
          <Lock size={11} aria-hidden />
          Evidence resident in africa-south1 · every verdict needs your confirmation · usage is
          audited
        </div>
      )}

      <div aria-live="polite">
        {done && run && !expanded && (
          <div className="ai-panel-result">{renderResult()}</div>
        )}
        {done && run && expanded && (
          <div className="ai-expanded-note">
            <Maximize2 size={12} aria-hidden /> Review open in full view — press Esc to
            return.
          </div>
        )}
        {state.kind === 'stalled' && (
          <div className="ai-panel-result ai-warn">
            <AlertTriangle size={12} aria-hidden /> {state.message}
          </div>
        )}
        {state.kind === 'error' && (
          <div className="ai-panel-result ai-bad" role="alert">
            <AlertTriangle size={12} aria-hidden /> {state.message}
          </div>
        )}
      </div>
      {/* Expand: the full-height view. Portalled out of the panel, so opening a
          citation from here surfaces the evidence viewer above it. Every action
          is the same `onConfirm` the inline surface uses — the roomier layout
          does not change what gets recorded. */}
      {done && run && expanded && (
        <Flyout
          open
          onClose={() => setExpanded(false)}
          width="wide"
          title={`AI review — ${referenceNo}`}
          subtitle={`${run.proposals.length} proposal${run.proposals.length === 1 ? '' : 's'} · confirm or override each — nothing is recorded until you save`}
        >
          <div className="ai-panel-result ais-in-flyout">{renderResult()}</div>
        </Flyout>
      )}
    </div>
  );
}

function GroupIcon({ group }: { group: ActionGroup }): React.JSX.Element {
  const size = 13;
  if (group === 'attention') return <AlertTriangle size={size} aria-hidden />;
  if (group === 'missing') return <Search size={size} aria-hidden />;
  if (group === 'compliant') return <Check size={size} aria-hidden />;
  return <Ban size={size} aria-hidden />;
}

/**
 * The intermediate "proposed findings" view, shown once a run finishes.
 *
 * It joins each criterion proposal to its rubric label and groups the lot by
 * what the reviewer must DO — evidenced non-compliance first, then the
 * missing-evidence defaults, with the compliant and not-applicable groups folded
 * away. That is the whole point: the analysis becomes legible at a glance instead
 * of forcing the reviewer to scroll 33 rows of the working paper to find the two
 * that matter.
 *
 * It writes NOTHING. Every action calls the page's `onConfirm`, the same code
 * path the per-row "Use this verdict" button uses, so a proposal accepted here is
 * indistinguishable from one accepted in the table below — and manual overrides
 * are protected identically (onConfirm skips any row already decided).
 */
export function AiProposalSummary({
  proposals,
  criteria,
  decidedSeqs,
  disabled,
  onConfirm,
  onOpenCitation,
}: {
  proposals: AiProposal[];
  criteria: { seq: number; label: string }[];
  decidedSeqs?: ReadonlySet<number>;
  disabled: boolean;
  onConfirm?: (seqs?: number[]) => void;
  onOpenCitation?: (citation: AiProposal['citations'][number]) => void;
}): React.JSX.Element {
  const labelBySeq = new Map(criteria.map((c) => [c.seq, c.label]));
  const isDecided = (seq: number | null): boolean => seq !== null && !!decidedSeqs?.has(seq);

  // Bucket by actionability, preserving checklist order inside each bucket so the
  // summary reads top-to-bottom the same way the table below does.
  const groups = new Map<ActionGroup, AiProposal[]>();
  for (const p of [...proposals].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))) {
    const key = classifyProposal(p);
    const arr = groups.get(key) ?? [];
    arr.push(p);
    groups.set(key, arr);
  }

  return (
    <div className="ais-summary">
      <div className="ais-summary-head">Proposed findings</div>
      {GROUP_ORDER.map((key) => {
        const items = groups.get(key);
        if (!items || items.length === 0) return null;
        const meta = GROUP_META[key];
        const undecided = items
          .filter((p) => !isDecided(p.seq))
          .map((p) => p.seq)
          .filter((s): s is number => s !== null);
        return (
          <details key={key} className={`ais-group is-${meta.tone}`} open={meta.openByDefault}>
            <summary className="ais-group-head">
              <span className="ais-group-title">
                <GroupIcon group={key} />
                {meta.title}
                <span className="ais-group-count">{items.length}</span>
              </span>
              {onConfirm && undecided.length > 0 && (
                // Inside <summary>, so the default click would toggle the
                // disclosure — suppressed, since the button's job is to confirm,
                // not to collapse the group the reviewer is acting on.
                <button
                  type="button"
                  className="ais-group-confirm"
                  disabled={disabled}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onConfirm(undecided);
                  }}
                >
                  Confirm {undecided.length}
                </button>
              )}
            </summary>
            <p className="ais-group-blurb">{meta.blurb}</p>
            <ul className="ais-list">
              {items.map((p) => (
                <AiProposalRow
                  key={p.seq}
                  proposal={p}
                  label={labelBySeq.get(p.seq ?? -1) ?? `Criterion ${p.seq}`}
                  decided={isDecided(p.seq)}
                  disabled={disabled}
                  onConfirm={onConfirm}
                  onOpenCitation={onOpenCitation}
                />
              ))}
            </ul>
          </details>
        );
      })}
    </div>
  );
}

/** One proposal in the summary: humanised label, verdict pill, confidence,
 *  one-line rationale, citation chips that open the viewer, and a confirm. */
function AiProposalRow({
  proposal,
  label,
  decided,
  disabled,
  onConfirm,
  onOpenCitation,
}: {
  proposal: AiProposal;
  label: string;
  decided: boolean;
  disabled: boolean;
  onConfirm?: (seqs?: number[]) => void;
  onOpenCitation?: (citation: AiProposal['citations'][number]) => void;
}): React.JSX.Element {
  const band = confidenceBand(proposal.confidence);
  const pct = Math.round(proposal.confidence * 100);
  return (
    <li className={`ais-item${decided ? ' is-decided' : ''}`}>
      <div className="ais-item-top">
        <span className="ais-item-label">{label}</span>
        <Badge className={complianceClass(cesMapStatusToRegister(proposal.verdict))}>
          {proposal.verdict}
        </Badge>
      </div>
      <div className="ais-item-sub">
        <span className={`ais-conf ais-conf-${band.toLowerCase()}`}>
          {band} · {pct}%
        </span>
        {proposal.pfmaSection && <span className="ais-pfma">{proposal.pfmaSection}</span>}
        {proposal.suspiciousEvidence && (
          <span className="ais-suspect">
            <AlertTriangle size={11} aria-hidden /> source flagged — read before accepting
          </span>
        )}
      </div>
      {proposal.rationale && <p className="ais-item-why">{proposal.rationale}</p>}
      <div className="ais-item-foot">
        <div className="ais-cites">
          {proposal.missingEvidence ? (
            <span className="ais-nocite">No evidence located in the pack</span>
          ) : proposal.citations.length === 0 ? (
            <span className="ais-nocite">No citation</span>
          ) : (
            proposal.citations.map((c) => (
              <button
                key={`${c.doc}-${c.page}`}
                type="button"
                className="ai-cite"
                title={c.quote}
                aria-label={`Open evidence: ${c.doc}, page ${c.page}`}
                onClick={() => onOpenCitation?.(c)}
              >
                {c.doc} · p.{c.page}
              </button>
            ))
          )}
        </div>
        {decided ? (
          <span className="ais-item-done">
            <Check size={12} aria-hidden /> Confirmed
          </span>
        ) : (
          onConfirm &&
          proposal.seq !== null && (
            <button
              type="button"
              className="ais-use"
              disabled={disabled}
              onClick={() => onConfirm([proposal.seq as number])}
            >
              Use verdict
            </button>
          )
        )}
      </div>
    </li>
  );
}

/**
 * The per-criterion proposal, rendered under the reviewer's status dropdown.
 * The dropdown remains their control — this only offers a value.
 */
export function AiProposalBlock({
  proposal,
  confirmed,
  disabled,
  onConfirm,
  onOpenCitation,
}: {
  proposal: AiProposal;
  confirmed: boolean;
  disabled: boolean;
  onConfirm: () => void;
  /** Optional so the block can still be rendered directly (and tested) without
   *  wiring an evidence viewer. */
  onOpenCitation?: (citation: AiProposal['citations'][number]) => void;
}): React.JSX.Element {
  const band = confidenceBand(proposal.confidence);
  const pct = Math.round(proposal.confidence * 100);
  return (
    <div className="ai-prop">
      <div className="ai-prop-head">
        <span className="ai-prop-tag">
          <Sparkles size={9} aria-hidden /> AI
        </span>
        {/* The real compliance pill every other surface uses.
            ⚠️ NOT <CesStatusBadge>. CES statuses slug to `c-Non-Compliant-PFMA`,
            `c-Non-Compliant-Housekeeping-Issue` and friends — and NONE of those
            has a `.c-*` rule, so the engine's three most serious verdicts would
            all render as one neutral grey pill, visually identical to "not
            reviewed". Routing the verdict through cesMapStatusToRegister keeps
            the reviewer's own CES wording as the label while taking the colour
            from the register level, which is how compliance reads everywhere
            else in the app. */}
        <Badge className={complianceClass(cesMapStatusToRegister(proposal.verdict))}>
          {proposal.verdict}
        </Badge>
      </div>

      <div
        className={`ai-conf ai-conf-${band.toLowerCase()}`}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Confidence ${band}, ${pct} percent`}
      >
        <i style={{ width: `${pct}%` }} />
      </div>
      <div className="ai-prop-meta">
        {band} · {pct}%
      </div>

      {proposal.missingEvidence ? (
        // The deterministic default. Said plainly, because "we could not find it"
        // is a different claim from "we checked and it fails".
        <div className="ai-prop-missing">No evidence located — defaulted to PFMA.</div>
      ) : (
        <div className="ai-prop-cites">
          {proposal.citations.map((c) => (
            // A button, not a span: the chip opens the evidence at the cited
            // page. The page number is DERIVED from where the quote actually
            // sits in the OCR text, not taken from the model — see
            // aiGuard.locateCitation.
            <button
              key={`${c.doc}-${c.page}`}
              type="button"
              className="ai-cite"
              title={c.quote}
              aria-label={`Open evidence: ${c.doc}, page ${c.page}`}
              onClick={() => onOpenCitation?.(c)}
            >
              {c.doc} · p.{c.page}
            </button>
          ))}
        </div>
      )}

      {/* The statutory anchor, in mono. Carried on every proposal and never
          rendered until now. */}
      {proposal.pfmaSection && <div className="ai-prop-pfma">{proposal.pfmaSection}</div>}

      {proposal.suspiciousEvidence && (
        // An injected instruction can produce a perfectly verifiable citation, so
        // the reviewer is told the SOURCE is suspect even though the quote checks
        // out. Without this the attack is invisible to the human control.
        <div className="ai-prop-suspect">
          <AlertTriangle size={11} aria-hidden /> Cited document contains instruction-like text —
          read it before accepting.
        </div>
      )}

      {proposal.rationale && <div className="ai-prop-why">{proposal.rationale}</div>}

      {confirmed ? (
        <div className="ai-prop-done">
          <Check size={12} aria-hidden /> Confirmed by you
        </div>
      ) : (
        <Button size="sm" onClick={onConfirm} disabled={disabled}>
          Use this verdict
        </Button>
      )}
    </div>
  );
}
